<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.dashboard'),
                        href: meta.links.dashboard_index,
                    },
                    {
                        name: $tc('messages.teachers', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                <div class="sm:col-span-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.status')" for="active" />
                        <FormSelect
                            id="active"
                            required
                            v-model="form.active"
                            :options="options.active"
                            :null-option="false"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.active" class="mt-2" />
                    </div>
                </div>
            </div>
        </form>

        <div class="w-full flex space-x-4 justify-end">
            <ActionsPanel v-show="selectedUsers.length">
                <inertia-link
                    v-if="filters.active === 'active'"
                    method="delete"
                    as="button"
                    :href="meta.links.deactivateMultiple"
                    :data="{ users: selectedUsers }"
                    role="menuitem"
                    class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                        class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    >
                        <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z"></path>
                        <path
                            fill-rule="evenodd"
                            d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                            clip-rule="evenodd"
                        ></path>
                    </svg>
                    {{ $t('messages.deactivate') }}
                </inertia-link>
                <inertia-link
                    v-if="filters.active === 'inactive'"
                    method="post"
                    as="button"
                    :href="meta.links.activateMultiple"
                    :data="{ users: selectedUsers }"
                    role="menuitem"
                    class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                        class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                    >
                        <path
                            d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"
                        ></path>
                    </svg>
                    {{ $t('messages.activate') }}
                </inertia-link>
            </ActionsPanel>

            <AppButton element="InertiaLink" :href="meta.links.create">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <SelectAllNoneButtons
                                        :max-count="teachers.data.length"
                                        :selected-count="selectedUsers.length"
                                        @select-all="selectAll()"
                                        @select-none="selectedUsers = []"
                                        class="inline-flex"
                                    />
                                    <span>{{ $t('messages.name') }}</span>
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.group') }}
                                </th>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(teacher, index) in teachers.data"
                                :key="teacher.id"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    <input
                                        type="checkbox"
                                        name="selected-users"
                                        v-model="selectedUsers"
                                        :value="teacher.id"
                                        class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded mr-1"
                                    />
                                    {{ teacher.full_name }}
                                    <BadgePill
                                        v-for="role in teacher.roles"
                                        :key="`teacher_${teacher.full_name}_role_${role}`"
                                    >
                                        {{ role }}
                                    </BadgePill>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ teacher.group }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <OptionsPanel>
                                        <inertia-link
                                            v-if="teacher.active"
                                            method="delete"
                                            as="button"
                                            :href="teacher.links.deactivate"
                                            role="menuitem"
                                            class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                                class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                            >
                                                <path d="M4 3a2 2 0 100 4h12a2 2 0 100-4H4z"></path>
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M3 8h14v7a2 2 0 01-2 2H5a2 2 0 01-2-2V8zm5 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                            {{ $t('messages.deactivate') }}
                                        </inertia-link>
                                        <inertia-link
                                            v-else
                                            :href="teacher.links.activate"
                                            method="post"
                                            as="button"
                                            role="menuitem"
                                            class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                                aria-hidden="true"
                                                class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                            >
                                                <path
                                                    d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"
                                                ></path>
                                            </svg>
                                            {{ $t('messages.activate') }}
                                        </inertia-link>
                                        <inertia-link
                                            :href="teacher.links.subjects_edit"
                                            role="menuitem"
                                            class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                        >
                                            <svg
                                                class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                            {{ $tc('messages.subject', 2) }}
                                        </inertia-link>
                                        <inertia-link
                                            :href="teacher.links.roles_edit"
                                            role="menuitem"
                                            class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                        >
                                            <svg
                                                class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    fill-rule="evenodd"
                                                    d="M10 2a1 1 0 00-1 1v1a1 1 0 002 0V3a1 1 0 00-1-1zM4 4h3a3 3 0 006 0h3a2 2 0 012 2v9a2 2 0 01-2 2H4a2 2 0 01-2-2V6a2 2 0 012-2zm2.5 7a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm2.45 4a2.5 2.5 0 10-4.9 0h4.9zM12 9a1 1 0 100 2h3a1 1 0 100-2h-3zm-1 4a1 1 0 011-1h2a1 1 0 110 2h-2a1 1 0 01-1-1z"
                                                    clip-rule="evenodd"
                                                ></path>
                                            </svg>
                                            {{ $tc('messages.roles', 2) }}
                                        </inertia-link>
                                    </OptionsPanel>

                                    <InertiaSquareLinkButton
                                        action="show"
                                        :href="teacher.links.show"
                                        v-if="teacher.links.show"
                                    />
                                    <InertiaSquareLinkButton
                                        action="edit"
                                        :href="teacher.links.edit"
                                        v-if="teacher.links.edit"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="teachers" />
        </template>
    </PanelPage>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import OptionsPanel from '@/components/OptionsPanel.vue';
import ActionsPanel from '@/components/ActionsPanel.vue';
import BadgePill from '@/components/BadgePill.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import AppButton from '@/components/AppButton.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';

export default {
    components: {
        SelectAllNoneButtons,
        SubmitFormButton,
        AppButton,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        OptionsPanel,
        ActionsPanel,
        FormLabel,
        FormSelect,
        FormError,
        BadgePill,
    },
    layout: AdminLayout,
    name: 'Index',
    props: ['meta', 'teachers', 'filters', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
                active: this.filters.active,
            }),
            selectedUsers: [],
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        selectAll() {
            this.selectedUsers = this.teachers.data.map((teacher) => teacher.id);
        },
    },
};
</script>
